import { Eye, EyeOff } from 'lucide-react';
import { type ComponentProps, useState } from 'react';

import { cx } from '@/lib/cx';
import { Button } from '@/ui/primitives/button';
import { Input } from '@/ui/primitives/input';

type CampoSenhaProps = Omit<ComponentProps<'input'>, 'type'>;

/**
 * Campo de senha com o "olho" para conferir o que foi digitado. Recebe as mesmas props do
 * `Input` (inclusive `ref` e o retorno do `register`) e encaixa no `Campo`.
 *
 * O botão é um toggle (`aria-pressed`) de nome fixo — "Mostrar senha", pressionado = visível —
 * como pede o padrão ARIA de botão de alternância (nome que muda junto do estado confunde o
 * leitor de tela). O nome vem de texto `sr-only`, não de `aria-label`: assim
 * `getByLabel('Senha')` (e2e e testes) continua achando só o campo. Fica na ordem do Tab, depois
 * do campo; no clique do mouse não tira o foco do campo, para a pessoa seguir digitando.
 */
export function CampoSenha({ className, disabled, ...props }: CampoSenhaProps) {
  const [visivel, setVisivel] = useState(false);
  const Icone = visivel ? EyeOff : Eye;
  return (
    <div className="relative">
      <Input
        type={visivel ? 'text' : 'password'}
        disabled={disabled}
        className={cx('pr-10', className)}
        // Senha visível não vai para corretor nem sugestões do teclado do celular.
        spellCheck={false}
        autoCapitalize="none"
        autoCorrect="off"
        {...props}
      />
      <Button
        variante="fantasma"
        tamanho="icone-sm"
        aria-pressed={visivel}
        disabled={disabled}
        className="absolute inset-y-0 right-1 my-auto"
        onMouseDown={(evento) => {
          evento.preventDefault();
        }}
        onClick={() => {
          setVisivel((atual) => !atual);
        }}
      >
        <Icone aria-hidden className="animate-surgir" key={String(visivel)} />
        <span className="sr-only">Mostrar senha</span>
      </Button>
    </div>
  );
}
